import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Truck, CaretDown, CaretUp, CalendarCheck } from '@phosphor-icons/react';
import { formatDate } from '../utils/dateFormat';
import { DispatchEntryDialog } from '../components/DispatchEntryDialog';
import { DispatchEntriesTable } from '../components/DispatchEntriesTable';
import { SearchFilterBar, EmptySearchResult, LoadErrorState } from '../components/SearchFilterBar';

const STATUS_LABELS = {
  production_completed: 'Production Completed',
  scheduled_for_dispatch: 'Scheduled',
  partially_dispatched: 'Partially Dispatched',
  dispatched: 'Dispatched'
};

const STATUS_COLORS = {
  production_completed: 'bg-blue-100 text-blue-700',
  scheduled_for_dispatch: 'bg-yellow-100 text-yellow-700',
  partially_dispatched: 'bg-orange-100 text-orange-700',
  dispatched: 'bg-emerald-100 text-emerald-700'
};

const DISPATCH_SEARCH_FIELDS = [
  { key: 'search', label: 'Search', placeholder: 'JO number, customer, location...' },
  {
    key: 'statusFilter', label: 'Status', type: 'select',
    options: [
      { value: 'all', label: 'All Statuses' },
      { value: 'production_completed', label: 'Production Completed' },
      { value: 'scheduled_for_dispatch', label: 'Scheduled' },
      { value: 'partially_dispatched', label: 'Partially Dispatched' },
      { value: 'dispatched', label: 'Dispatched' },
    ]
  }
];

const DISPATCH_DEFAULT_FILTERS = { search: '', statusFilter: 'all' };

function WindowTracker({ jo }) {
  const total = jo.no_of_windows || 0;
  const dispatched = jo.windows_dispatched || 0;
  const pending = jo.windows_pending ?? (total - dispatched);
  const pct = total > 0 ? Math.round((dispatched / total) * 100) : 0;

  return (
    <div className="flex items-center gap-2 text-xs">
      <div className="flex gap-1.5 items-center">
        <span className="text-zinc-500">Win:</span>
        <span className="font-bold text-zinc-800">{dispatched}/{total}</span>
      </div>
      <div className="w-16 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
        <div className="h-full bg-orange-500 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      {pending > 0 && <span className="text-orange-600 font-bold">{pending} left</span>}
    </div>
  );
}

function ScheduleDispatchDialog({ open, onOpenChange, selectedJo, driverMaster, vehicleMaster, onSubmit, loading }) {
  const [form, setForm] = useState({
    schedule_date: '', driver_name: '', driver_contact: '', vehicle_number: '',
    dispatch_type: 'full', windows_to_dispatch: '', remarks: ''
  });

  const totalWindows = selectedJo?.no_of_windows || 0;
  const alreadyDispatched = selectedJo?.windows_dispatched || 0;
  const remaining = Math.max(0, totalWindows - alreadyDispatched);

  const handleSubmit = () => {
    if (!form.schedule_date) { toast.error('Please enter a schedule date'); return; }
    onSubmit(selectedJo, {
      ...form,
      windows_to_dispatch: form.dispatch_type === 'partial' ? parseInt(form.windows_to_dispatch) || null : null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarCheck size={22} weight="bold" className="text-blue-600" />
            Schedule Dispatch - {selectedJo?.jo_number}
          </DialogTitle>
          <DialogDescription className="sr-only">Schedule a future dispatch</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="bg-zinc-50 p-3 rounded-sm text-sm">
            <p><strong>{selectedJo?.customer_name}</strong> | {selectedJo?.work_location}</p>
            <p className="text-xs text-zinc-500 mt-1">{totalWindows} windows | Dispatched: {alreadyDispatched} | Pending: {remaining}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Scheduled Date *</Label>
              <Input data-testid="schedule-date-input" type="date" value={form.schedule_date} onChange={e => setForm(f => ({ ...f, schedule_date: e.target.value }))} className="h-10" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Dispatch Type</Label>
              <Select value={form.dispatch_type} onValueChange={v => setForm(f => ({ ...f, dispatch_type: v }))}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Dispatch</SelectItem>
                  <SelectItem value="partial">Partial Dispatch</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.dispatch_type === 'partial' && (
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Windows to Dispatch (max {remaining})</Label>
              <Input data-testid="schedule-windows-input" type="number" min={1} max={remaining} value={form.windows_to_dispatch}
                onChange={e => setForm(f => ({ ...f, windows_to_dispatch: e.target.value }))} placeholder={`1 - ${remaining}`} className="h-10" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Driver Name</Label>
              {driverMaster.length > 0 ? (
                <Select value={form.driver_name} onValueChange={v => {
                  const d = driverMaster.find(dr => dr.name === v);
                  setForm(f => ({ ...f, driver_name: v, driver_contact: d?.mobile || '' }));
                }}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Select driver" /></SelectTrigger>
                  <SelectContent>{driverMaster.map(d => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input value={form.driver_name} onChange={e => setForm(f => ({ ...f, driver_name: e.target.value }))} placeholder="Driver name" className="h-10" />
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Driver Contact</Label>
              <Input value={form.driver_contact} onChange={e => setForm(f => ({ ...f, driver_contact: e.target.value }))} placeholder="Contact number" className="h-10" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Vehicle Number</Label>
            {vehicleMaster.length > 0 ? (
              <Select value={form.vehicle_number} onValueChange={v => setForm(f => ({ ...f, vehicle_number: v }))}>
                <SelectTrigger className="h-10"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent>{vehicleMaster.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input value={form.vehicle_number} onChange={e => setForm(f => ({ ...f, vehicle_number: e.target.value }))} placeholder="e.g. KA-01-AB-1234" className="h-10" />
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Remarks</Label>
            <Textarea value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} placeholder="Optional..." rows={2} />
          </div>
          <Button data-testid="confirm-schedule" onClick={handleSubmit} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-11">
            {loading ? 'Scheduling...' : 'Schedule Dispatch'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InstallationDispatch() {
  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filters, setFilters] = useState(DISPATCH_DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DISPATCH_DEFAULT_FILTERS);
  const [dispatchDialog, setDispatchDialog] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [selectedJo, setSelectedJo] = useState(null);
  const [dispatchEntries, setDispatchEntries] = useState({});
  const [expandedJo, setExpandedJo] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [driverMaster, setDriverMaster] = useState([]);
  const [vehicleMaster, setVehicleMaster] = useState([]);

  const [dispatchForm, setDispatchForm] = useState({
    dispatch_date: '',
    driver_name: '',
    driver_contact: '',
    vehicle_number: '',
    dispatch_type: 'full',
    windows_dispatched: '',
    remarks: ''
  });

  const navigate = useNavigate();

  const openDispatchDialog = useCallback((jo) => {
    setSelectedJo(jo);
    const remaining = Math.max(0, (jo.no_of_windows || 0) - (jo.windows_dispatched || 0));
    setDispatchForm({
      dispatch_date: '',
      driver_name: '',
      driver_contact: '',
      vehicle_number: '',
      dispatch_type: remaining > 0 ? 'full' : 'full',
      windows_dispatched: '',
      remarks: ''
    });
    setDispatchDialog(true);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [joRes, driverRes, vehicleRes] = await Promise.all([
        api.get('/api/job-orders?list_type=dispatch_all'),
        api.get('/api/masters/drivers'),
        api.get('/api/masters/vehicles'),
      ]);
      const dispatchJobs = joRes.data;
      setJobOrders(dispatchJobs);
      setDriverMaster(driverRes.data || []);
      setVehicleMaster(vehicleRes.data || []);

      const joParam = searchParams.get('jo');
      if (joParam) {
        const jo = dispatchJobs.find(j => j.jo_number === joParam);
        if (jo) openDispatchDialog(jo);
      }
    } catch (err) {
      if (err?.isAuthError) return;
      setLoadError(true);
      toast.error('Failed to load job orders');
    } finally {
      setLoading(false);
    }
  }, [searchParams, openDispatchDialog]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = () => { setAppliedFilters({...filters}); };
  const handleReset = (cleared) => { setFilters(cleared); setAppliedFilters(cleared); };
  const handleRefresh = () => { setLoadError(false); fetchData(); };

  const filteredOrders = jobOrders.filter(jo => {
    if (appliedFilters.statusFilter !== 'all' && jo.status !== appliedFilters.statusFilter) return false;
    if (appliedFilters.search) {
      const s = appliedFilters.search.toLowerCase();
      return jo.jo_number.toLowerCase().includes(s) ||
        jo.customer_name.toLowerCase().includes(s) ||
        (jo.work_location || '').toLowerCase().includes(s);
    }
    return true;
  });

  const totalWindows = useMemo(() => filteredOrders.reduce((s, jo) => s + (jo.no_of_windows || 0), 0), [filteredOrders]);
  const totalDispatched = useMemo(() => filteredOrders.reduce((s, jo) => s + (jo.windows_dispatched || 0), 0), [filteredOrders]);
  const totalSqft = useMemo(() => filteredOrders.reduce((s, jo) => s + (jo.total_sqft || 0), 0), [filteredOrders]);

  const fetchEntries = async (joNumber) => {
    try {
      const { data } = await api.get(`/api/job-orders/${joNumber}/dispatch-entries`);
      setDispatchEntries(prev => ({ ...prev, [joNumber]: data }));
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load dispatch entries');
    }
  };

  const toggleExpand = (joNumber) => {
    if (expandedJo === joNumber) {
      setExpandedJo(null);
    } else {
      setExpandedJo(joNumber);
      if (!dispatchEntries[joNumber]) fetchEntries(joNumber);
    }
  };

  const handleCreateEntry = async () => {
    if (!dispatchForm.dispatch_date || !dispatchForm.driver_name || !dispatchForm.vehicle_number) {
      toast.error('Please fill dispatch date, driver name, and vehicle number');
      return;
    }
    if (dispatchForm.dispatch_type === 'partial' && (!dispatchForm.windows_dispatched || dispatchForm.windows_dispatched <= 0)) {
      toast.error('Please enter number of windows being dispatched');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/api/job-orders/${selectedJo.jo_number}/dispatch-entry`, {
        ...dispatchForm,
        windows_dispatched: dispatchForm.dispatch_type === 'partial' ? parseInt(dispatchForm.windows_dispatched) : null
      });
      toast.success(`Dispatch entry created for ${selectedJo.jo_number}`);
      setDispatchDialog(false);
      fetchData();
      if (expandedJo === selectedJo.jo_number) fetchEntries(selectedJo.jo_number);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create dispatch entry');
    } finally {
      setActionLoading(false);
    }
  };

  const handleScheduleDispatch = async (jo, formData) => {
    setActionLoading(true);
    try {
      await api.post(`/api/job-orders/${jo.jo_number}/schedule-dispatch`, formData);
      toast.success(`Dispatch scheduled for ${jo.jo_number}`);
      setScheduleDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule dispatch');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-zinc-950">Dispatch Management</h1>
          <p className="text-sm text-zinc-500 mt-2">Manage vehicle allocation, driver assignment, and dispatch entries</p>
        </div>

        <SearchFilterBar
          fields={DISPATCH_SEARCH_FIELDS}
          filters={filters}
          setFilters={setFilters}
          onSearch={handleSearch}
          onReset={handleReset}
          onRefresh={handleRefresh}
          loading={loading}
          resultCount={filteredOrders.length}
        />

        {/* Totals */}
        <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-around flex-wrap gap-4">
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Showing</p>
                <p className="text-2xl font-light text-zinc-950">{filteredOrders.length}</p>
              </div>
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
                <p className="text-2xl font-light text-zinc-950">{totalWindows}</p>
              </div>
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Dispatched</p>
                <p className="text-2xl font-light text-orange-600">{totalDispatched}</p>
              </div>
              <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Sqft</p>
                <p className="text-2xl font-light text-zinc-950">{totalSqft.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Job Orders List */}
        {(() => {
          if (loading) return <div className="text-center py-12">Loading...</div>;
          if (loadError) return <LoadErrorState onRetry={handleRefresh} />;
          if (filteredOrders.length === 0) {
            return <EmptySearchResult onReset={() => handleReset(DISPATCH_DEFAULT_FILTERS)} />;
          }
          return (
            <div className="space-y-3">
              {filteredOrders.map(jo => (
                <Card key={jo.jo_number} className="border-zinc-300 rounded-sm shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span
                            className="text-lg font-bold text-zinc-950 cursor-pointer hover:text-orange-600"
                            onClick={() => navigate(`/job-orders/${jo.jo_number}`)}
                          >{jo.jo_number}</span>
                          <Badge className={`${STATUS_COLORS[jo.status] || 'bg-zinc-100 text-zinc-700'} text-xs font-bold`}>
                            {STATUS_LABELS[jo.status] || jo.status.replace(/_/g, ' ')}
                          </Badge>
                          <WindowTracker jo={jo} />
                        </div>
                        <p className="text-sm text-zinc-600 mt-1">{jo.customer_name} | {jo.work_location}</p>
                        <div className="flex gap-4 mt-1 text-xs text-zinc-500 flex-wrap">
                          <span>Delivery: {formatDate(jo.delivery_date) || jo.delivery_date}</span>
                          <span>Exec: {jo.executive_name}</span>
                          {jo.dispatch_schedule_date && <span className="text-blue-600 font-bold">Scheduled: {formatDate(jo.dispatch_schedule_date) || jo.dispatch_schedule_date}</span>}
                          {jo.last_dispatch_date && <span>Last dispatch: {formatDate(jo.last_dispatch_date) || jo.last_dispatch_date}</span>}
                          {jo.last_vehicle_number && <span>Vehicle: {jo.last_vehicle_number}</span>}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {jo.status === 'production_completed' && (
                          <Button
                            size="sm"
                            data-testid={`schedule-btn-${jo.jo_number}`}
                            onClick={() => { setSelectedJo(jo); setScheduleDialog(true); }}
                            variant="outline"
                            className="text-xs h-8"
                          ><CalendarCheck size={14} className="mr-1" />Schedule</Button>
                        )}
                        {jo.status !== 'dispatched' && (
                          <Button
                            size="sm"
                            data-testid={`dispatch-btn-${jo.jo_number}`}
                            onClick={() => openDispatchDialog(jo)}
                            className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-8"
                          >
                            <Truck size={14} className="mr-1" />
                            Dispatch
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`expand-${jo.jo_number}`}
                          onClick={() => toggleExpand(jo.jo_number)}
                          className="text-xs h-8"
                        >
                          {expandedJo === jo.jo_number ? <CaretUp size={16} /> : <CaretDown size={16} />}
                          Entries
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Dispatch Entries */}
                    {expandedJo === jo.jo_number && (
                      <div className="mt-4 border-t border-zinc-200 pt-4">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Dispatch Entries</p>
                        <DispatchEntriesTable entries={dispatchEntries[jo.jo_number]} />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          );
        })()}

        <DispatchEntryDialog
          open={dispatchDialog}
          onOpenChange={setDispatchDialog}
          selectedJo={selectedJo}
          dispatchForm={dispatchForm}
          setDispatchForm={setDispatchForm}
          driverMaster={driverMaster}
          vehicleMaster={vehicleMaster}
          onSubmit={handleCreateEntry}
          actionLoading={actionLoading}
        />

        <ScheduleDispatchDialog
          open={scheduleDialog}
          onOpenChange={setScheduleDialog}
          selectedJo={selectedJo}
          driverMaster={driverMaster}
          vehicleMaster={vehicleMaster}
          onSubmit={handleScheduleDispatch}
          loading={actionLoading}
        />
      </div>
    </Layout>
  );
}
